import React from "react";

import { Edit, MoreVertical, Trash2 } from "lucide-react";

import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "../ui/dropdown-menu";

/**
 * Common Action Menu (Three dots dropdown)
 *
 * @param {function} onEdit - Callback for edit action
 * @param {function} onDelete - Callback for delete action
 * @param {boolean} canEdit - Boolean indicating if the user has edit permission
 * @param {boolean} canDelete - Boolean indicating if the user has delete permission
 * @param {React.ReactNode} customActions - Optional actions to render at the top
 * @param {React.ReactNode} quickLinks - Optional quick links to render with a label
 * @param {string} quickLinksLabel - Label for the quick links section (default: "Quick Links")
 * @param {string} menuWidth - Width class for the dropdown menu
 * @param {React.ReactNode} children - Legacy prop, rendered at the very top
 */
const ActionMenu = ({
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  customActions,
  quickLinks,
  quickLinksLabel = "Quick Links",
  menuWidth = "w-48",
  children
}) => {
  const hasEdit = canEdit && onEdit;
  const hasDelete = canDelete && onDelete;

  if (!hasEdit && !hasDelete && !customActions && !quickLinks && !children) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full hover:bg-slate-100 dark:hover:bg-yale-blue-800"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={menuWidth} onClick={(e) => e.stopPropagation()}>
        {children}

        {customActions && (
          <>
            {customActions}
            {(hasEdit || quickLinks || hasDelete) && <DropdownMenuSeparator />}
          </>
        )}

        {hasEdit && (
          <>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Edit className="w-4 h-4 text-yale-blue-500" />
              <span>Edit</span>
            </DropdownMenuItem>
            {(quickLinks || hasDelete) && <DropdownMenuSeparator />}
          </>
        )}

        {quickLinks && (
          <>
            <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">{quickLinksLabel}</div>
            {quickLinks}
            {hasDelete && <DropdownMenuSeparator />}
          </>
        )}

        {hasDelete && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="flex items-center gap-2 text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20 cursor-pointer"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionMenu;
